import { cn } from '@/components/ui/utils';

interface LogoMarkProps {
  size?: number;
  className?: string;
}

/**
 * The TP mark drawn flat in one solid colour (the brand accent by default).
 * The original artwork is a gradient PNG, so it is used as a mask and filled with
 * `currentColor`: same shape, no gradient.
 */
export function LogoMark({ size = 28, className }: LogoMarkProps) {
  return (
    <span
      aria-hidden="true"
      className={cn('inline-block shrink-0 bg-current text-brand', className)}
      style={{
        width: size,
        height: size,
        WebkitMaskImage: 'url(/assets/TrenioAppLogo.webp)',
        maskImage: 'url(/assets/TrenioAppLogo.webp)',
        WebkitMaskSize: 'contain',
        maskSize: 'contain',
        WebkitMaskRepeat: 'no-repeat',
        maskRepeat: 'no-repeat',
        WebkitMaskPosition: 'center',
        maskPosition: 'center',
      }}
    />
  );
}

interface LogoProps {
  size?: number;
  className?: string;
  /** Hide the wordmark below the `sm` breakpoint */
  compact?: boolean;
  label?: string;
}

/** The mark as drawn: the original gradient artwork, untouched. Used where the brand should stand out (sidebar). */
export function LogoOriginal({ height = 40, className }: { height?: number; className?: string }) {
  return (
    <img
      src="/assets/TrenioAppLogo.webp"
      alt=""
      aria-hidden="true"
      draggable={false}
      className={cn('shrink-0 select-none object-contain', className)}
      style={{ height, width: Math.round(height * (1252 / 979)) }}
    />
  );
}

/** Mark + wordmark, used in the public navbar, footer and auth pages. */
export function Logo({ size = 28, className, compact = false, label }: LogoProps) {
  return (
    <span className={cn('inline-flex items-center gap-2.5', className)}>
      <LogoOriginal height={size} />
      {/* line box as tall as the mark, so the word sits on the mark's optical centre */}
      <span
        className={cn('flex items-center text-[18px] font-semibold tracking-[-0.02em] text-foreground', compact && 'hidden sm:flex')}
        style={{ height: size }}
      >
        TrenioApp
      </span>
      {label && (
        <span className="hidden text-[13px] leading-none text-muted-foreground sm:inline">{label}</span>
      )}
    </span>
  );
}
